import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import "./App.css";
import { useTranslation } from "react-i18next";
import "./i18n";

// Eager: auth / shared / insurance screens and framework components.
import LoginScreen from "./pages/login-screen/LoginScreen";
import ForgotPassword from "./pages/forgot-password/ForgotPassword";
import ResetPassword from "./pages/reset-password/ResetPassword";
import UploadDocuments from "./pages/upload-documents/UploadDocuments";
import UploadRequiredDocuments from "./pages/upload-required-documents/UploadRequiredDocuments";
import Introduction from "./pages/introduction/Introduction";
import Header from "./components/header/Header";
import InsuranceLogin from "./pages/insurance-login/InsuranceLogin";
import InsuranceDashboard from "./pages/insurance-dashboard/InsuranceDashboard";
import InsuranceDoctorDashboard from "./pages/insurance-doctor-dashboard/InsuranceDoctorDashboard";
import PatientInformation from "./components/patient-info/PatientInformation";
import InsuranceLabDashboard from "./pages/insurance-lab-dashboard/InsuranceLabDashboard";
import InsuranceClaimHistoryDashboard from "./pages/insurance-claimHistory-dashboard/InsuranceClaimHistoryDashboard";
import AddAddress from "./pages/add-address/AddAddress";
import SessionManager from "./components/session-manager/SessionManager";
import ProtectedRoute from "./components/route-protection/ProtectedRoute";
import AccountDeactivatedModal from "./components/account-deactivated/AccountDeactivatedModal";
import NotFound from "./pages/not-found/NotFound";

// Lazy: doctor routes (role_id 4) — split into their own chunks, loaded on demand.
const DashboardPage = lazy(() => import("./pages/dashboard/Dashboard"));
const Appointments = lazy(() => import("./pages/appointments/Appointments"));
const DoctorCalendar = lazy(() => import("./pages/calender/DoctorCalendar"));
const DocumentsUnderProcess = lazy(
  () => import("./pages/document-under-process/DocumentsUnderProcess"),
);
const Patients = lazy(() => import("./pages/patients/Patients"));
const PatientsProfile = lazy(
  () => import("./pages/patients-profile/PatientsProfile"),
);
const PatientRegistration = lazy(
  () => import("./pages/patient-registration/PatientRegistration"),
);
const Wallet = lazy(() => import("./pages/wallet/Wallet"));
const WithdrawFromWallet = lazy(
  () => import("./pages/withdraw-from-wallet/WithdrawFromWallet"),
);
const LoyaltyPoints = lazy(
  () => import("./pages/loyalty-points/LoyaltyPoints"),
);
const Notifications = lazy(
  () => import("./pages/notifications/Notifications"),
);
const ChangePassword = lazy(
  () => import("./pages/change-password/ChangePassword"),
);
const MyProfile = lazy(() => import("./pages/my-profile/MyProfile"));
const DoctorRegistration = lazy(
  () => import("./pages/doctor-registration/DoctorRegistration"),
);
const DoctorRules = lazy(() => import("./pages/doctor-rules/DoctorRules"));
const Setting = lazy(() => import("./pages/setting/Setting"));
const Faqs = lazy(() => import("./pages/faqs/faq"));
const DoctorSupport = lazy(
  () => import("./pages/doctor-support/DoctorSupport"),
);
const Chat = lazy(() => import("./pages/chat/Chat"));
const HomeVisit = lazy(() => import("./pages/home-visit/HomeVisit"));
const Online = lazy(() => import("./pages/online/Online"));

// Lazy: pharmacy routes (role_id 5).
const PharamacyInventory = lazy(
  () => import("./pages/pharamacy-Inventory/PharamacyInventory"),
);
const PharmacyDashboard = lazy(
  () => import("./pages/Pharmacy-Dashboard/PharmacyDashboard"),
);
const PharmacyAllOrder = lazy(
  () => import("./pages/Pharmacy-All-Order/PharmacyAllOrder"),
);
const PharmacyOrdersRequest = lazy(
  () => import("./pages/pharmacy-orders-request/PharmacyOrdersRequest"),
);
const PharmacyOrdersHistory = lazy(
  () => import("./pages/pharmacy-orders-history/PharmacyOrdersHistory"),
);
const OrderDetails = lazy(
  () => import("./pages/PharmacyOrderDetails/OrderDetails"),
);
const PharmacyOrderHistoryDetails = lazy(
  () =>
    import("./pages/PharmacyOrderHistoryDetails/PharmacyOrderHistoryDetails"),
);
const PharmacyOrderRequestDetails = lazy(
  () =>
    import("./pages/PharmacyOrderRequestDetails/PharmacyOrderRequestDetails"),
);
const PharmacyEditManageProduct = lazy(
  () =>
    import("./pages/pharmacy-edit-manage-product/PharmacyEditManageProduct"),
);
const PharmacyAddMedicineToInventory = lazy(
  () =>
    import("./pages/pharmacy-add-medicine-to-inventory/PharmacyAddMedicineToInventory"),
);
const UploadDocumentsPharma = lazy(
  () => import("./pages/pharmacy-upload-document/UploadDocumentsPharma"),
);
const PharmacyAccountPayments = lazy(
  () => import("./pages/pharmacy-account-payments/PharmacyAccountPayments"),
);
const PharmacyWithdrawFromWallet = lazy(
  () => import("./pages/pharmacy-withdraw-from-wallet/WithdrawFromWallet"),
);
const PharmacyChat = lazy(() => import("./pages/pharmacy-chat/PharmacyChat"));
const PharmacySetting = lazy(
  () => import("./pages/pharmacy-setting/PharmacySetting"),
);
const PharmacyChangePassword = lazy(
  () => import("./pages/pharmacy-change-password/PharmacyChangePassword"),
);
const PharmacyProfile = lazy(
  () => import("./pages/pharmacy-profile/PharmacyProfile"),
);

function LayoutWithHeader({ changeLanguage }) {
  // Track the role selected on the login screen so the Header can hide the language dropdown
  // for the Pharmacy role (it should only show for Doctor).
  const [loginRole, setLoginRole] = useState("Doctor");
  return (
    <>
      <Header changeLanguage={changeLanguage} loginRole={loginRole} />
      <Outlet context={{ loginRole, setLoginRole }} />{" "}
      {/* renders child routes */}
    </>
  );
}

function App() {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    document.dir = lang === "ar" ? "rtl" : "ltr";
  };

  // Set document direction on mount based on current language
  useEffect(() => {
    document.dir = i18n.language === "ar" ? "rtl" : "ltr";
  }, [i18n.language]);

  return (
    <SessionManager>
      <div className="App">
        <Router>
          <Suspense
            fallback={
              <div
                style={{
                  minHeight: "100vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#ffffff",
                }}
              >
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    border: "4px solid #e5e7eb",
                    borderTopColor: "#0cbdbd",
                    borderRadius: "50%",
                    animation: "app-spin 0.8s linear infinite",
                  }}
                />
              </div>
            }
          >
            <Routes>
              {/* Doctor routes (role_id: 4) - Protected */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/appointments"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <Appointments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/appointments-calender"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <DoctorCalendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/documents-under-process"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <DocumentsUnderProcess />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patients"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <Patients />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patients-profile"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <PatientsProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient-registration"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <PatientRegistration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wallet"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <Wallet />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/withdraw-from-wallet"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <WithdrawFromWallet />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/additional-income"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <LoyaltyPoints />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-profile"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <MyProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor-rules"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <DoctorRules />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor-registration"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <DoctorRegistration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/setting"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <Setting />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <ChangePassword />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/help-and-support"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <Faqs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chat"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <Chat />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor-support"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <DoctorSupport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/home-visit"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <HomeVisit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/online"
                element={
                  <ProtectedRoute
                    allowedRoleId={4}
                    redirectTo="/pharmacy-dashboard"
                  >
                    <Online />
                  </ProtectedRoute>
                }
              />

              {/* With header */}
              <Route
                element={<LayoutWithHeader changeLanguage={changeLanguage} />}
              >
                <Route path="/" element={<LoginScreen />} />
                <Route path="/introduction" element={<Introduction />} />
                <Route
                  path="/upload-required-documents"
                  element={<UploadRequiredDocuments />}
                />
                <Route path="/upload-documents" element={<UploadDocuments />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
              </Route>

              {/* Pharmacy routes (role_id: 5) - Protected */}
              <Route
                path="/pharmacy-upload-documents"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <UploadDocumentsPharma />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-dashboard"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharmacyDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-all-order"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharmacyAllOrder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-orders-request"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharmacyOrdersRequest />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-orders-history"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharmacyOrdersHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-order-details"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <OrderDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-order-history-details"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharmacyOrderHistoryDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-order-request-details"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharmacyOrderRequestDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ducts"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharamacyInventory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-manage-product"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharmacyEditManageProduct />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-add-medicine-to-inventory"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharmacyAddMedicineToInventory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-account-payments"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharmacyAccountPayments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-withdraw-from-wallet"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharmacyWithdrawFromWallet />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-chat"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharmacyChat />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-setting"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharmacySetting />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-change-password"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharmacyChangePassword />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy-profile"
                element={
                  <ProtectedRoute allowedRoleId={5} redirectTo="/dashboard">
                    <PharmacyProfile />
                  </ProtectedRoute>
                }
              />

              {/* Insurance Routes */}
              <Route
                element={<LayoutWithHeader changeLanguage={changeLanguage} />}
              >
                <Route path="/i_login" element={<InsuranceLogin />} />
                <Route path="/i_dashboard" element={<InsuranceDashboard />} />
                <Route
                  path="/idoctor_dashboard"
                  element={<InsuranceDoctorDashboard />}
                />
                <Route
                  path="/ilab_dashboard"
                  element={<InsuranceLabDashboard />}
                />
                <Route path="/patient-info" element={<PatientInformation />} />
                <Route
                  path="/iclaim-history_dashboard"
                  element={<InsuranceClaimHistoryDashboard />}
                />
              </Route>

              {/* The route below is not insurance route so do not use this add address route */}
              <Route
                element={<LayoutWithHeader changeLanguage={changeLanguage} />}
              >
                <Route path="/add-address" element={<AddAddress />} />
              </Route>

              {/* Catch-all: any unmatched doctor/pharmacy (or other) route → 404 page */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          {/* App-wide blocking modal shown when the account is deactivated. */}
          <AccountDeactivatedModal />
        </Router>
      </div>
    </SessionManager>
  );
}

export default App;
